import { deactivateUser } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { UserListItem } from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

interface DeactivateDialogProps {
  user: UserListItem | null;
  onClose: () => void;
  onDeactivated: (user: UserListItem, sessionsRevoked: number) => void;
}

export function DeactivateDialog({ user, onClose, onDeactivated }: DeactivateDialogProps) {
  const { t } = useTranslation('users');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setSubmitting(false);
      setError(null);
    }
  }, [user]);

  async function confirm() {
    if (!user) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await deactivateUser(user.id);
      onDeactivated(user, result.sessionsRevoked);
      onClose();
    } catch (err) {
      setError(translateApiError(err, t, { fallback: t('deactivateDialog.error') }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={!!user} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('deactivateDialog.title')}</DialogTitle>
          <DialogDescription>{t('deactivateDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="mt-6 rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-4 py-3">
          <p className="text-[12px] uppercase tracking-widest text-jq-graphite">
            {t('deactivateDialog.affectedAccount')}
          </p>
          <p className="mt-1 break-all font-mono text-[13px] text-jq-charcoal">{user?.email}</p>
        </div>

        {error && (
          <p className="mt-4 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('deactivateDialog.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={confirm}
            disabled={submitting}
          >
            {submitting ? t('deactivateDialog.confirming') : t('deactivateDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
